import {
  Button,
  Chip,
  Css,
  HbLoadingSpinner,
  Icon,
  ModalBody,
  ModalFooter,
  Palette,
  TestIds,
  Tooltip,
  useModal,
  useTestIds,
} from "@homebound/beam";
import { useState } from "react";
import { Link } from "react-router-dom";
import { MaterialVariantsForPlaceholderTliFilter, Order } from "src/generated/graphqlTypes";
import {
  PlaceholderProductSearch_MaterialVariantFragment,
  PlaceholderProductSearch_PossibleMaterialsQuery,
  usePlaceholderProductSearch_PossibleMaterialsQuery,
  usePlaceholderProductSearch_RequirementsTliQuery,
} from "src/routes/libraries/design-catalog/components/PlaceholderProductSearch.generated";
import { PRODUCT_FALLBACK_IMG_URL } from "src/routes/libraries/product-catalog/components/product-images-viewer/ProductImageViewer";
import { createMaterialDetailsUrl } from "src/RouteUrls";
import { fail } from "src/utils";
import { ProductSearchFilters } from "./ProductSearchFilters";

export type PlaceholderMaterialSelection =
  | { kind: "materials"; ids: string[] }
  | { kind: "noMaterialVariant" }
  | undefined;

// The full selection payload reported on card click, before it's saved. Exported so callers of
// `StagedProductSearchModal` can type their `onSave` handler.
export type MaterialSelectionResult =
  | { kind: "material"; id: string; mv: PlaceholderProductSearch_MaterialVariantFragment }
  | { kind: "noMaterialVariant" };
type OnMaterialSelected = (selection: MaterialSelectionResult) => void;

export type PlaceholderStagedSelection = { kind: "material"; id: string } | { kind: "noMaterialVariant" } | undefined;

// Export for testing
export type PlaceholderProductSearchProps = {
  placeholderTliId: string;
  /** Used to highlight the currently-selected MaterialVariants (or noMaterialVariant). */
  currentMaterials: PlaceholderMaterialSelection;
  onMaterialSelected: OnMaterialSelected;
  showNoMaterialVariant: boolean;
  /** Highlights a selection that's been picked but not yet saved. */
  stagedSelection?: PlaceholderStagedSelection;
};

/**
 * Provides "what products can fit in this placeholder?".
 *
 * This component is used both by the "Edit Personalization Option" within a modal, and the
 * "Design Package Configurator" as a standalone page.
 */
export function PlaceholderProductSearch(props: PlaceholderProductSearchProps) {
  const { placeholderTliId, onMaterialSelected, currentMaterials, showNoMaterialVariant, stagedSelection } = props;
  // Pull in the Placeholder requirements (like `36in Gas Range`) and possible Attribute Filters (like Finish, Installation Type, etc.)
  const query = usePlaceholderProductSearch_RequirementsTliQuery({ variables: { placeholderTliId } });
  const tid = useTestIds({}, "PlaceholderProductSearch");
  const [filter, setFilter] = useState<MaterialVariantsForPlaceholderTliFilter>({
    takeoffLineItemId: placeholderTliId,
  });
  const { data, loading: isLoadingPossibilities } = usePlaceholderProductSearch_PossibleMaterialsQuery({
    variables: { filter, orderBy: { lastUsedAt: Order.Desc } },
  });

  const placeholderTli = query.data?.takeoffLineItem;
  if (!placeholderTli) {
    return <HbLoadingSpinner />;
  }
  const requirementMavs = placeholderTli.materialVariant?.materialAttributeValues ?? [];
  const materialVariants = data?.materialVariantsForPlaceholderTli ?? [];

  return (
    <>
      <div css={Css.df.fdc.gap2.$}>
        <div css={Css.df.jcsb.aie.$}>
          <div css={Css.df.aic.gap2.$}>
            <h1 css={Css.xl.$}>{placeholderTli.item.name}</h1>
            {!isLoadingPossibilities && (
              <span css={Css.sm.gray700.$}>{`${materialVariants.length} results found`}</span>
            )}
          </div>
        </div>
        <div css={Css.df.gap1.mb1.$}>
          {requirementMavs.map((mav) => (
            <Chip key={mav.id} text={`${mav.dimension.name}: ${mav.textValue}`} compact />
          ))}
        </div>
      </div>
      <ProductSearchFilters
        placeholderTliId={placeholderTliId}
        placeholderMv={placeholderTli.materialVariant! ?? fail("placeholders should always have a material variant")}
        placeholderItem={placeholderTli.item}
        setFilter={setFilter}
      />
      <div css={Css.df.fdc.gap2.md.$}>
        {isLoadingPossibilities ? (
          <HbLoadingSpinner />
        ) : (
          <div css={Css.dg.gtc("repeat(auto-fill, minmax(340px, 1fr))").gap3.$}>
            {showNoMaterialVariant && (
              <NoProductCard
                tid={tid}
                currentMaterial={currentMaterials}
                isStaged={stagedSelection?.kind === "noMaterialVariant"}
                onMaterialSelected={onMaterialSelected}
              />
            )}
            {materialVariants.map((mv) => (
              <MaterialCard
                key={mv.id}
                tid={tid}
                currentMaterial={currentMaterials}
                isStaged={stagedSelection?.kind === "material" && stagedSelection.id === mv.id}
                onMaterialSelected={onMaterialSelected}
                mv={mv}
              />
            ))}
          </div>
        )}
      </div>
    </>
  );
}

type StagedProductSearchModalProps = {
  placeholderTliId: string;
  showNoMaterialVariant: boolean;
  currentMaterials: PlaceholderMaterialSelection;
  onSave: (selection: MaterialSelectionResult) => void;
};

/**
 * Wraps `PlaceholderProductSearch` in a modal with the staged-pick + Cancel/Save footer shared by both
 * `ProductSearchModal` callers (`EditPersonalizationOptionPage` and `VariantsStep`) — only the
 * domain-specific "what do I do with this selection" logic differs between them, so that stays with the caller.
 */
export function StagedProductSearchModal(props: StagedProductSearchModalProps) {
  const { placeholderTliId, showNoMaterialVariant, currentMaterials, onSave } = props;
  const { closeModal } = useModal();
  const [staged, setStaged] = useState<MaterialSelectionResult | undefined>();
  return (
    <>
      {/* Skipping `ModalTitle` b/c PlaceholderProductSearch has its own effectively-a-title UX */}
      <ModalBody>
        <PlaceholderProductSearch
          placeholderTliId={placeholderTliId}
          showNoMaterialVariant={showNoMaterialVariant}
          currentMaterials={currentMaterials}
          stagedSelection={staged}
          onMaterialSelected={setStaged}
        />
      </ModalBody>
      <ModalFooter>
        <Button label="Cancel" variant="quaternary" onClick={closeModal} />
        <Button
          label="Save"
          disabled={!staged && "Select a product first"}
          onClick={() => {
            onSave(staged!);
            closeModal();
          }}
        />
      </ModalFooter>
    </>
  );
}

// Shared shell so the "empty slot" card and product cards always stay visually identical.
const selectableCardCss = Css.bgWhite.br8.ba.bw2.bcGray200.p3.df.aic.gap4.cursorPointer.bshBasic.onHover.bshHover.$;
const highlightedCardCss = Css.bcBlue600.bgBlue50.$;

/** Shows a card that means "no product is assigned to this slot". */
function NoProductCard(props: {
  tid: TestIds;
  currentMaterial: PlaceholderMaterialSelection;
  isStaged: boolean;
  onMaterialSelected: OnMaterialSelected;
}) {
  const { tid, currentMaterial, isStaged, onMaterialSelected } = props;
  const isAssigned = currentMaterial?.kind === "noMaterialVariant";
  return (
    <div
      /* The "Empty Product Slot" row/card/selection */
      css={{
        ...selectableCardCss,
        // highlight the card if it's either the currently-assigned option or the staged (unsaved) pick
        ...(isAssigned || isStaged ? highlightedCardCss : {}),
      }}
      onClick={() => onMaterialSelected({ kind: "noMaterialVariant" })}
      {...tid.noProductCard}
    >
      <div css={Css.wPx(120).hPx(120).fs0.br4.bgGray100.df.aic.jcc.$}>
        <Icon icon="xCircle" color={Palette.Gray700} inc={4} />
      </div>
      <div css={Css.df.aic.gap1.$}>
        <div css={Css.smSb.$}>Add empty product slot</div>
        <Tooltip
          title={`
            Sometimes a product type isn't offered with certain Package Options (e.g., no refrigerator
            with the Essential package). In these cases, tell Design Packages to leave it empty!
          `}
        >
          <Icon icon="infoCircle" color={Palette.Gray700} inc={2} />
        </Tooltip>
      </div>
    </div>
  );
}

function MaterialCard(props: {
  tid: TestIds;
  currentMaterial: PlaceholderMaterialSelection;
  isStaged: boolean;
  onMaterialSelected: OnMaterialSelected;
  mv: PlaceholderProductSearch_PossibleMaterialsQuery["materialVariantsForPlaceholderTli"][number];
}) {
  const { tid, currentMaterial, isStaged, onMaterialSelected, mv } = props;
  const isAssigned = currentMaterial?.kind === "materials" && currentMaterial.ids.includes(mv.id);
  return (
    <div
      css={{
        ...selectableCardCss,
        // highlight the card if it's either the currently-assigned material or the staged (unsaved) pick
        ...(isAssigned || isStaged ? highlightedCardCss : {}),
      }}
      onClick={() => onMaterialSelected({ kind: "material", id: mv.id, mv })}
      {...tid.materialCard}
    >
      <div css={Css.wPx(120).hPx(120).fs0.br4.ba.bcGray200.oh.$}>
        <img
          alt={`${mv.displayName}-product`}
          src={mv.featuredImage?.asset?.previewUrl ?? PRODUCT_FALLBACK_IMG_URL}
          css={Css.w100.h100.objectContain.$}
        />
      </div>
      <div css={Css.df.fdc.f1.oh.$}>
        {mv.listing?.brand?.name && <div css={Css.sm.gray600.$}>{mv.listing.brand.name}</div>}
        <Tooltip title={mv.displayName}>
          <div css={Css.sm.lineClamp(3).$} {...tid.displayName}>
            {mv.displayName}
          </div>
        </Tooltip>
        {/* Whole card is clickable so wrap link with a div to keep target area confined to text */}
        <div>
          <Link
            to={createMaterialDetailsUrl(mv.id)}
            target="_blank"
            onClick={(e) => e.stopPropagation()}
            css={Css.xsSb.gray600.tdu.mt1.onHover.gray900.$}
            {...tid.viewInCatalog}
          >
            More info
          </Link>
        </div>
      </div>
    </div>
  );
}
